import { z } from 'zod';

export const ConnectionIntakeRhythm = z.enum(['weekdays', 'weekends', 'daily', 'rare']);
export type ConnectionIntakeRhythm = z.infer<typeof ConnectionIntakeRhythm>;

export const ConnectionIntakeWindow = z.enum(['morning', 'afternoon', 'evening']);
export type ConnectionIntakeWindow = z.infer<typeof ConnectionIntakeWindow>;

export const OneOnOneActivity = z.enum(['coffee', 'walk', 'sport', 'talk']);
export type OneOnOneActivity = z.infer<typeof OneOnOneActivity>;

export const AutoMeetingStatus = z.enum([
  'INTAKE',
  'PROPOSED',
  'CONFIRMED',
  'DECLINED',
  'EXPIRED',
  'RESCHEDULING',
]);
export type AutoMeetingStatus = z.infer<typeof AutoMeetingStatus>;

export const AutoMeetingResponse = z.enum(['PENDING', 'ACCEPTED', 'DECLINED', 'MODIFY']);
export type AutoMeetingResponse = z.infer<typeof AutoMeetingResponse>;

export const AutoMeetingRespondAction = z.enum(['accept', 'decline', 'next_slot']);
export type AutoMeetingRespondAction = z.infer<typeof AutoMeetingRespondAction>;

const Hm = z.string().regex(/^\d{2}:\d{2}$/);

export const AvailabilityWindowDto = z.object({
  weekday: z.number().int().min(1).max(7),
  startHm: Hm,
  endHm: Hm,
});
export type AvailabilityWindowDto = z.infer<typeof AvailabilityWindowDto>;

export const ReplaceAvailabilityInput = z.object({
  windows: z.array(AvailabilityWindowDto).max(21),
});
export type ReplaceAvailabilityInput = z.infer<typeof ReplaceAvailabilityInput>;

export const ConnectionIntakeAnswerInput = z.object({
  step: z.number().int().min(1).max(5),
  value: z.string().min(1).max(32),
  fingerprint: z.string().min(4).max(8),
});
export type ConnectionIntakeAnswerInput = z.infer<typeof ConnectionIntakeAnswerInput>;

export const ConnectionIntakeDto = z.object({
  step: z.number().int(),
  rhythm: ConnectionIntakeRhythm.nullable(),
  window: ConnectionIntakeWindow.nullable(),
  nearTerm: z.record(z.string(), z.enum(['free', 'busy'])).nullable(),
  activities: z.array(OneOnOneActivity),
  fingerprint: z.string(),
  completed: z.boolean(),
});
export type ConnectionIntakeDto = z.infer<typeof ConnectionIntakeDto>;

export const AvailabilityDto = z.object({
  windows: z.array(AvailabilityWindowDto),
  intake: ConnectionIntakeDto.nullable(),
});
export type AvailabilityDto = z.infer<typeof AvailabilityDto>;

export const AutoMeetingPeerDto = z.object({
  userId: z.uuid(),
  displayName: z.string(),
  photoUrl: z.string().nullable(),
});
export type AutoMeetingPeerDto = z.infer<typeof AutoMeetingPeerDto>;

/** Четыре грани подготовки к встрече — только для зрителя, чужой JSON сюда не кладём. */
export const MeetingPrepDto = z.object({
  talk: z.string(),
  avoid: z.string(),
  stance: z.string(),
  likes: z.string(),
});
export type MeetingPrepDto = z.infer<typeof MeetingPrepDto>;

export const AutoMeetingDto = z.object({
  id: z.uuid(),
  status: AutoMeetingStatus,
  myResponse: AutoMeetingResponse,
  theirResponse: AutoMeetingResponse,
  sourceEventId: z.uuid(),
  sourceEventTitle: z.string(),
  startsAt: z.iso.datetime().nullable(),
  endsAt: z.iso.datetime().nullable(),
  activity: OneOnOneActivity.nullable(),
  rationale: z.string().nullable(),
  peer: AutoMeetingPeerDto,
  meetingEventId: z.uuid().nullable(),
  expiresAt: z.iso.datetime().nullable(),
  prep: MeetingPrepDto.nullable(),
});
export type AutoMeetingDto = z.infer<typeof AutoMeetingDto>;

export const AutoMeetingsListDto = z.object({
  items: z.array(AutoMeetingDto),
});
export type AutoMeetingsListDto = z.infer<typeof AutoMeetingsListDto>;

export const AutoMeetingRespondInput = z.object({
  action: AutoMeetingRespondAction,
});
export type AutoMeetingRespondInput = z.infer<typeof AutoMeetingRespondInput>;

export const AUTO_MEETING_DURATION_MINUTES = 90;
export const AUTO_MEETING_CAP_PER_DAY = 3;
export const AUTO_MEETING_COOLDOWN_DAYS = 14;
export const AUTO_MEETING_SHARED_EVENT_DAYS = 14;
export const AUTO_MEETING_OFFER_TTL_HOURS = 48;
export const AUTO_MEETING_DEFAULT_START = '18:00';
export const AUTO_MEETING_DEFAULT_END = '21:00';
/** Горизонт «скоро встреча» для стула SYNC и read_meeting_prep. */
export const AUTO_MEETING_PREP_HORIZON_HOURS = 36;
